import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { api } from '../axios-instance'

interface UpdateProfileData {
  firstname?: string
  lastname?: string
  gender?: string
  phone?: string
  address?: string
  nationality?: string
  password?: string
  avatar?: string
}

// API call function
const updateProfileAPI = async (data: UpdateProfileData): Promise<any> => {
  // console.log('📤 Frontend updateUserProfile - Updating user profile with data:', data)
  
  // Filter data to only include fields that are safe to update
  const filteredData: any = {}
  
  // Only include non-empty string fields or explicitly provided fields
  if (data.firstname?.trim()) filteredData.firstname = data.firstname.trim()
  if (data.lastname?.trim()) filteredData.lastname = data.lastname.trim()
  if (data.gender) filteredData.gender = data.gender
  if (data.address !== undefined) filteredData.address = data.address // Allow empty string
  if (data.phone !== undefined) filteredData.phone = data.phone // Allow empty string
  if (data.nationality !== undefined) filteredData.nationality = data.nationality // Allow empty string
  if (data.password?.trim()) filteredData.password = data.password.trim()
  if (data.avatar) filteredData.avatar = data.avatar
  
  // console.log('📤 Filtered data being sent:', filteredData)
  
  const response = await api.patch('/user-controller/updateprofile', filteredData)
  // console.log('✅ Frontend updateUserProfile - Profile update response:', response.data)
  return response.data
}

// Custom hook using useMutation
export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: updateProfileAPI,
    retry: false, // Don't retry failed requests
    onSuccess: () => {
      // Invalidate all userProfile queries (regardless of JWT parameter)
      queryClient.invalidateQueries({ queryKey: ['userProfile'] })
      toast({
        title: 'Profile updated',
        description: 'Your profile has been updated successfully.',
      })
    },
    onError: (error: any) => {
      // console.error('❌ Frontend updateUserProfile - Failed to update user profile:', error)
      
      // Handle specific error types
      let errorMessage = 'Failed to update profile'
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.'
      } else if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.message
        if (backendMessage) {
          if (backendMessage.includes('Parent') && backendMessage.includes('not found')) {
            errorMessage = 'There is a data inconsistency issue. Please contact an administrator.'
          } else {
            errorMessage = Array.isArray(backendMessage) ? backendMessage.join(', ') : backendMessage
          }
        } else {
          errorMessage = 'Invalid data provided'
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

// Legacy service object for backward compatibility
export const userService = {
  updateUserProfile: updateProfileAPI,
}

export default userService
